import { useLab } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { labOrderFormSchema, type LabOrderFormValues } from '@schema/labOrder.schema'
import type { LabOrderData, LabOrderIncludedResource, LabOrderUpdatePayload } from '@type/lab.type'
import { FormButton, Modal, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import {
  buildTestsAttribute,
  newFreeTextOption,
  optionsFromOrderLines,
  searchTests,
  type TestLineOption
} from '@util/testCatalogue'
import { useCallback, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { GroupBase } from 'react-select'
import { buildPlacedLines } from './LabDetails.helpers'
import { OptionTag, TestLineRows } from './TestLineRows'

type Props = {
  onClose: () => void
  id: string
  defaultData?: LabOrderData
  included?: LabOrderIncludedResource[]
}

/** Existing lines as picker options: facility tests keep their id, free text its name; both keep `performed_at`. */
export function testsFromOrder(
  order: LabOrderData,
  included: LabOrderIncludedResource[] | undefined
): TestLineOption[] {
  return optionsFromOrderLines(buildPlacedLines(order, included))
}

const EditLabOrder = ({ onClose, id, defaultData, included }: Props) => {
  const { singleLabOrder, updateLabOrder } = useLab({ id })
  const order = (singleLabOrder?.data?.type === 'lab_order' ? singleLabOrder.data : defaultData) as
    | LabOrderData
    | undefined
  const resolvedIncluded = (singleLabOrder?.included ?? included) as LabOrderIncludedResource[] | undefined

  const defaultTests = useMemo(() => (order ? testsFromOrder(order, resolvedIncluded) : []), [order, resolvedIncluded])

  const methods = useForm<LabOrderFormValues>({
    defaultValues: { lab_tests: [], note: order?.attributes.note ?? '' },
    resolver: yupResolver(labOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = methods

  useEffect(() => {
    if (!order) return
    reset({
      lab_tests: defaultTests,
      note: order.attributes.note ?? ''
    })
  }, [defaultTests, order, reset])

  const loadLabTestOptions = useCallback((inputValue: string) => searchTests('lab', inputValue), [])

  const onSubmit = async (form: LabOrderFormValues) => {
    if (!id) return
    if (String(order?.attributes.status ?? '').toLowerCase() !== 'pending') {
      showToast('Only pending lab orders can be edited')
      return
    }

    const payload: LabOrderUpdatePayload = {
      data: {
        type: 'lab_order',
        attributes: {
          note: form.note?.trim() ?? '',
          tests: buildTestsAttribute(form.lab_tests, 'lab')
        }
      }
    }

    try {
      await updateLabOrder({ formVal: payload, id })
      showToast('Lab order updated successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit lab test">
        <form
          className="flex w-full min-w-0 max-w-[530px] flex-col gap-4 overflow-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4 overflow-auto p-6">
            <Controller
              control={control}
              name="lab_tests"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Select<TestLineOption, true, GroupBase<TestLineOption>>
                    borderRadius={'15px'}
                    defaultOptions
                    error={error?.message}
                    formatOptionLabel={(option, { context }) => (
                      <span>
                        {option.label}
                        {context === 'menu' ? <OptionTag option={option} /> : null}
                      </span>
                    )}
                    getNewOptionData={(inputValue, optionLabel) => newFreeTextOption(inputValue, optionLabel)}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => String(o.value)}
                    isAsync
                    isCreatable
                    isMulti
                    label="Test"
                    loadOptions={loadLabTestOptions}
                    onChange={opts => onChange((opts as TestLineOption[] | null) ?? [])}
                    placeholder="Search tests (e.g. FBC, malaria) or type a name"
                    value={value as TestLineOption[]}
                  />
                  <TestLineRows kind="lab" lines={(value as TestLineOption[]) ?? []} onChange={onChange} />
                </>
              )}
            />

            <TextAreaField label="Note to lab" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default EditLabOrder
